import { useEffect, useState } from 'react';
import { PageHeader, Panel } from '../components/Page';
import { EmptyState, ErrorState, InlineError, SkeletonText } from '../components/Feedback';
import { CheckboxField, SelectField, SliderField, ToggleField } from '../components/Form';
import { AccountLookupField } from '../components/AccountLookup';
import { RequireRole } from '../lib/auth';
import { aiSettings, learningAccounts } from '../lib/endpoints';
import { useAsync } from '../lib/hooks';
import { useToast } from '../lib/toast';
import { errorMessage } from '../lib/api';
import { GatewayRole } from '../lib/enums';
import { formatDateTime } from '../lib/format';
import type { AiSettingsDto, UpdateAiSettingsRequest } from '../lib/types';

/**
 * The contract stores thresholds as `decimal` without stating the scale. If every threshold the
 * API returns is <= 1 the values are treated as fractions (0.85) and converted for the sliders;
 * otherwise they are already percentages. Whatever scale came in is the scale sent back.
 */
function usesFractionScale(settings: AiSettingsDto): boolean {
  return [
    settings.autoApproveConfidenceThreshold,
    settings.reviewRequiredConfidenceThreshold,
    settings.vendorMatchConfidenceThreshold,
    settings.lineItemConfidenceThreshold,
  ].every((value) => value <= 1);
}

interface Draft extends UpdateAiSettingsRequest {}

function toDraft(settings: AiSettingsDto): Draft {
  return {
    autoApproveConfidenceThreshold: settings.autoApproveConfidenceThreshold,
    reviewRequiredConfidenceThreshold: settings.reviewRequiredConfidenceThreshold,
    vendorMatchConfidenceThreshold: settings.vendorMatchConfidenceThreshold,
    matchVendorByName: settings.matchVendorByName,
    matchVendorByTaxId: settings.matchVendorByTaxId,
    matchVendorByEmail: settings.matchVendorByEmail,
    autoCreateMissingVendor: settings.autoCreateMissingVendor,
    extractLineItems: settings.extractLineItems,
    lineItemConfidenceThreshold: settings.lineItemConfidenceThreshold,
    consolidateSimilarLines: settings.consolidateSimilarLines,
    allowInvoiceLineIerpAiExceptions: settings.allowInvoiceLineIerpAiExceptions,
    blockDuplicateDocuments: settings.blockDuplicateDocuments,
    defaultExpenseAccountId: settings.defaultExpenseAccountId,
    defaultExpenseAccountName: settings.defaultExpenseAccountName,
    defaultApAccountId: settings.defaultApAccountId,
    defaultApAccountName: settings.defaultApAccountName,
    defaultPaymentAccountId: settings.defaultPaymentAccountId,
    defaultPaymentAccountName: settings.defaultPaymentAccountName,
    // Older tenant rows can carry an empty string; the select needs one of the three valid values.
    defaultExpensePaymentType: settings.defaultExpensePaymentType || 'Cash',
    attachSourceDocumentToQbo: settings.attachSourceDocumentToQbo,
  };
}

const EXPENSE_PAYMENT_TYPE_OPTIONS = [
  { value: 'Cash', label: 'Cash' },
  { value: 'Check', label: 'Check' },
  { value: 'CreditCard', label: 'Credit card' },
];

// AccountLookupField, accountMatchesRole and accountOptionLabel now live in
// components/AccountLookup.tsx, shared with the Vendor Invoice Intake line editor.

function AiSettingsContent(): JSX.Element {
  const toast = useToast();
  const state = useAsync((signal) => aiSettings.get(signal), []);
  const accountsState = useAsync((signal) => learningAccounts.allEligible(signal), []);

  const [draft, setDraft] = useState<Draft | null>(null);
  const [fractionScale, setFractionScale] = useState(true);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (state.data) {
      setDraft(toDraft(state.data));
      setFractionScale(usesFractionScale(state.data));
      setDirty(false);
    }
  }, [state.data]);

  const set = <K extends keyof Draft>(key: K, value: Draft[K]) => {
    setDraft((current) => (current ? { ...current, [key]: value } : current));
    setDirty(true);
  };

  const toPercentValue = (value: number) => Math.round(fractionScale ? value * 100 : value);
  const fromPercentValue = (value: number) => (fractionScale ? value / 100 : value);

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    setSaveError(null);
    try {
      const updated = await aiSettings.update(draft);
      if (updated) state.setData(updated);
      setDirty(false);
      toast.success('AI settings saved', 'New documents will use these thresholds.');
    } catch (error) {
      const message = errorMessage(error);
      setSaveError(message);
      toast.error('Save failed', message);
    } finally {
      setSaving(false);
    }
  };

  if (state.loading && !state.data) {
    return (
      <>
        <PageHeader title="AI Settings" />
        <Panel>
          <SkeletonText lines={8} />
        </Panel>
      </>
    );
  }

  if (state.error && !state.data) {
    return (
      <>
        <PageHeader title="AI Settings" />
        <ErrorState message="AI settings could not be loaded." detail={state.error} onRetry={state.reload} />
      </>
    );
  }

  if (!draft || !state.data) {
    return <EmptyState title="No AI settings returned" />;
  }

  const autoApprove = toPercentValue(draft.autoApproveConfidenceThreshold);
  const reviewRequired = toPercentValue(draft.reviewRequiredConfidenceThreshold);
  const thresholdsInverted = reviewRequired > autoApprove;

  return (
    <>
      <PageHeader
        title="AI Settings"
        description="How the gateway interprets the iERP AI Bot's extractions."
        actions={
          <>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => {
                setDraft(toDraft(state.data!));
                setDirty(false);
              }}
              disabled={!dirty || saving}
            >
              Discard changes
            </button>
            <button type="button" className="btn btn-primary" onClick={save} disabled={!dirty || saving}>
              {saving ? 'Saving…' : 'Save settings'}
            </button>
          </>
        }
      />

      <InlineError message={saveError} />

      {thresholdsInverted ? (
        <div className="banner banner-warn" role="alert">
          <div className="banner-body">
            <p className="banner-title">Thresholds overlap</p>
            <p className="banner-detail">
              The review threshold ({reviewRequired}%) is above the auto-approve threshold ({autoApprove}%).
              Documents between the two will always land in review.
            </p>
          </div>
        </div>
      ) : null}

      <div className="grid-2">
        <Panel title="Confidence thresholds" description="Scored against the bot's overall readiness score.">
          <SliderField
            label="Auto-approve at or above"
            value={autoApprove}
            onChange={(value) => set('autoApproveConfidenceThreshold', fromPercentValue(value))}
            hint="Documents at or above this score can post without a human, subject to sync rules."
          />
          <SliderField
            label="Require review below"
            value={reviewRequired}
            onChange={(value) => set('reviewRequiredConfidenceThreshold', fromPercentValue(value))}
            hint="Anything below this score is held for an operator."
          />
          <SliderField
            label="Vendor match minimum"
            value={toPercentValue(draft.vendorMatchConfidenceThreshold)}
            onChange={(value) => set('vendorMatchConfidenceThreshold', fromPercentValue(value))}
            hint="A vendor match weaker than this is treated as unmatched."
          />
          <p className="field-hint">
            Stored as {fractionScale ? 'fractions (0–1)' : 'percentages (0–100)'}; the scale is preserved on
            save.
          </p>
        </Panel>

        <Panel title="Vendor matching">
          <CheckboxField
            label="Match by vendor name"
            checked={draft.matchVendorByName}
            onChange={(checked) => set('matchVendorByName', checked)}
          />
          <CheckboxField
            label="Match by tax identifier"
            checked={draft.matchVendorByTaxId}
            onChange={(checked) => set('matchVendorByTaxId', checked)}
            hint="The strongest signal when vendors share similar trading names."
          />
          <CheckboxField
            label="Match by email address"
            checked={draft.matchVendorByEmail}
            onChange={(checked) => set('matchVendorByEmail', checked)}
          />
          <CheckboxField
            label="Create missing vendors automatically"
            checked={draft.autoCreateMissingVendor}
            onChange={(checked) => set('autoCreateMissingVendor', checked)}
            hint="When off, documents with an unknown vendor wait for a human."
          />
        </Panel>
      </div>

      <Panel title="Line items">
        <div className="grid-2">
          <div>
            <CheckboxField
              label="Extract line items"
              checked={draft.extractLineItems}
              onChange={(checked) => set('extractLineItems', checked)}
              hint="When off the gateway posts a single summary line."
            />
            <CheckboxField
              label="Consolidate similar lines"
              checked={draft.consolidateSimilarLines}
              onChange={(checked) => set('consolidateSimilarLines', checked)}
              hint="Merges lines with the same description and account."
            />
            <CheckboxField
              label="Allow iERP AI line-item exception handling"
              checked={draft.allowInvoiceLineIerpAiExceptions}
              onChange={(checked) => set('allowInvoiceLineIerpAiExceptions', checked)}
              warning={
                <div className="licence-warning" role="note">
                  <strong>Requires an iERP AI Learning licence.</strong> iERP AI enforces licensing
                  regardless of this setting — without the licence the request is refused and the
                  document falls back to the standard line extraction path.
                </div>
              }
            />
          </div>
          <div>
            <SliderField
              label="Line item confidence minimum"
              value={toPercentValue(draft.lineItemConfidenceThreshold)}
              onChange={(value) => set('lineItemConfidenceThreshold', fromPercentValue(value))}
              hint="Lines below this score are flagged for review rather than posted silently."
              disabled={!draft.extractLineItems}
            />
          </div>
        </div>
      </Panel>

      <div className="grid-2">
        <Panel
          title="Default QuickBooks accounts"
          description={
            state.data.connectorCompanyName
              ? `Used when a line has no account of its own. Choices come from ${state.data.connectorCompanyName}.`
              : 'Used when a line has no account of its own. Connect QuickBooks and pull its Chart of Accounts first.'
          }
        >
          {accountsState.error ? (
            <InlineError message={`QuickBooks accounts could not be loaded: ${accountsState.error}`} />
          ) : !accountsState.loading && (accountsState.data?.length ?? 0) === 0 ? (
            <div className="banner banner-warn banner-inline" role="alert">
              <div className="banner-body">
                <p className="banner-title">No Chart of Accounts is available yet</p>
                <p className="banner-detail">
                  Open Accounts and pull the connected company's Chart of Accounts before choosing posting defaults.
                </p>
              </div>
            </div>
          ) : null}
          <div className="form-grid">
            <AccountLookupField
              searchLabel="Find expense account"
              label="Expense account"
              role="expense"
              accounts={accountsState.data ?? []}
              loading={accountsState.loading}
              selectedId={draft.defaultExpenseAccountId}
              selectedName={draft.defaultExpenseAccountName}
              onChange={(id, name) => {
                setDraft((current) =>
                  current ? { ...current, defaultExpenseAccountId: id, defaultExpenseAccountName: name } : current,
                );
                setDirty(true);
              }}
            />
            <AccountLookupField
              searchLabel="Find accounts payable account"
              label="Accounts payable account"
              role="ap"
              accounts={accountsState.data ?? []}
              loading={accountsState.loading}
              selectedId={draft.defaultApAccountId}
              selectedName={draft.defaultApAccountName}
              onChange={(id, name) => {
                setDraft((current) =>
                  current ? { ...current, defaultApAccountId: id, defaultApAccountName: name } : current,
                );
                setDirty(true);
              }}
            />
            <AccountLookupField
              searchLabel="Find payment account"
              label="Payment account (bank or credit card)"
              role="payment"
              accounts={accountsState.data ?? []}
              loading={accountsState.loading}
              selectedId={draft.defaultPaymentAccountId}
              selectedName={draft.defaultPaymentAccountName}
              onChange={(id, name) => {
                setDraft((current) =>
                  current ? { ...current, defaultPaymentAccountId: id, defaultPaymentAccountName: name } : current,
                );
                setDirty(true);
              }}
            />
            <p className="field-hint form-span-2">
              Account IDs are no longer typed manually. The gateway saves the QuickBooks ID behind the selected
              account and validates it against this company's current Chart of Accounts before saving.
            </p>
            <p className="field-hint form-span-2">
              Expense (Purchase) posting takes the money from the payment account, so that choice is restricted
              to bank and credit card accounts. Bills use the separate accounts payable account.
            </p>
            <SelectField
              label="Expense payment type"
              value={draft.defaultExpensePaymentType}
              onChange={(value) => set('defaultExpensePaymentType', value)}
              options={EXPENSE_PAYMENT_TYPE_OPTIONS}
              className="form-span-2"
            />
          </div>
        </Panel>

        <Panel title="Safety and attachments">
          <ToggleField
            label="Block suspected duplicate documents"
            checked={draft.blockDuplicateDocuments}
            onChange={(checked) => set('blockDuplicateDocuments', checked)}
          />
          <p className="field-hint">
            Documents that look like one already posted are routed to approval instead of posting.
          </p>
          <ToggleField
            label="Attach the source document to QuickBooks records"
            checked={draft.attachSourceDocumentToQbo}
            onChange={(checked) => set('attachSourceDocumentToQbo', checked)}
          />
          <p className="field-hint">
            Keeps the original PDF with the bill in QuickBooks. Operators can override this per document.
          </p>

          {state.data.modifiedUtc ? (
            <p className="panel-footnote">
              Last changed {formatDateTime(state.data.modifiedUtc)}
              {state.data.modifiedByEmail ? ` by ${state.data.modifiedByEmail}` : ''}.
            </p>
          ) : null}
        </Panel>
      </div>
    </>
  );
}

/** Admin-only screen; the guard is repeated here so it holds however the page is rendered. */
export function AiSettingsPage(): JSX.Element {
  return (
    <RequireRole minRole={GatewayRole.Admin}>
      <AiSettingsContent />
    </RequireRole>
  );
}
